import { Link, usePage } from "@inertiajs/react"
import {
  AlertTriangle,
  LayoutDashboard,
  Shield,
  ShieldCheck,
  Users,
} from "lucide-react"
import type { ReactNode } from "react"

import type { AdminSharedProps } from "@/types/serializers"

const navItems = [
  { href: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin/users", label: "Usuarios", icon: Users },
  { href: "/admin/reports", label: "Reportes", icon: AlertTriangle },
  {
    href: "/admin/verifications",
    label: "Verificaciones",
    icon: ShieldCheck,
  },
]

export default function AdminLayout({ children }: { children: ReactNode }) {
  const page = usePage()
  const url = page.url
  const adminData = (page.props as Record<string, unknown>).admin as
    | AdminSharedProps
    | undefined
  const pendingReportsCount = adminData?.pending_reports_count ?? 0
  const pendingVerificationsCount = adminData?.pending_verifications_count ?? 0

  return (
    <div className="bg-background flex min-h-svh">
      <aside className="border-border bg-card hidden w-64 shrink-0 border-r md:block">
        <div className="border-border flex h-14 items-center gap-2 border-b px-4">
          <Shield className="text-krown-300 h-5 w-5" />
          <span className="text-foreground text-sm font-semibold">
            Admin KrownFT
          </span>
        </div>
        <nav className="flex flex-col gap-1 p-3">
          {navItems.map((item) => {
            const isActive =
              url === item.href ||
              (item.href !== "/admin" && url.startsWith(item.href))
            const badgeCount =
              item.href === "/admin/reports"
                ? pendingReportsCount
                : item.href === "/admin/verifications"
                  ? pendingVerificationsCount
                  : 0
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-krown-500/20 text-krown-300 font-medium"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
                {badgeCount > 0 && (
                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
                    {badgeCount}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="border-border bg-card flex h-14 items-center justify-between border-b px-6 md:hidden">
          <div className="flex items-center gap-2">
            <Shield className="text-krown-300 h-5 w-5" />
            <span className="text-foreground text-sm font-semibold">Admin</span>
          </div>
          <nav className="flex gap-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="text-muted-foreground hover:bg-secondary relative rounded-lg p-2"
              >
                <item.icon className="h-5 w-5" />
                {((item.href === "/admin/reports" && pendingReportsCount > 0) ||
                  (item.href === "/admin/verifications" &&
                    pendingVerificationsCount > 0)) && (
                  <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {item.href === "/admin/reports"
                      ? pendingReportsCount
                      : pendingVerificationsCount}
                  </span>
                )}
              </Link>
            ))}
          </nav>
        </header>

        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  )
}
